/**
 * EventModal Component
 * Modal for creating and editing calendar events
 * Note: This is a simplified version - full modal implementation is in the main CalendarPage
 * TODO: Extract full EventModal with all features once CalendarPage refactor is complete
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { UserIcon, TrashIcon, EnvelopeIcon, PhoneIcon, DocumentTextIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { CalendarEvent, EventCategory } from '@/types/calendar';
import { TimePicker } from './TimePicker';
import { MiniCalendar } from './MiniCalendar';

// Context types for displaying lead/client info
interface EventContextLead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

interface EventContextClient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

interface EventContextQuote {
  id: string;
  number: string;
  unitName?: string;
}

interface EventModalProps {
  show: boolean;
  selectedEvent: CalendarEvent | null;
  newEvent: Partial<CalendarEvent>;
  eventCategories: EventCategory[];
  showStartDateCalendar: boolean;
  showEndDateCalendar: boolean;
  showStartTimePicker: boolean;
  showEndTimePicker: boolean;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  onNewEventChange: (event: Partial<CalendarEvent>) => void;
  setShowStartDateCalendar: (show: boolean) => void;
  setShowEndDateCalendar: (show: boolean) => void;
  setShowStartTimePicker: (show: boolean) => void;
  setShowEndTimePicker: (show: boolean) => void;
  // Context props for displaying lead/client info
  leadContext?: EventContextLead;
  clientContext?: EventContextClient;
  quoteContext?: EventContextQuote;
}

export const EventModal: React.FC<EventModalProps> = ({
  show,
  selectedEvent,
  newEvent,
  eventCategories,
  showStartDateCalendar,
  showEndDateCalendar,
  showStartTimePicker,
  showEndTimePicker,
  onClose,
  onSave,
  onDelete,
  onNewEventChange,
  setShowStartDateCalendar,
  setShowEndDateCalendar,
  setShowStartTimePicker,
  setShowEndTimePicker,
  leadContext,
  clientContext,
  quoteContext,
}) => {
  // Validation state
  const [errors, setErrors] = useState<{ title?: string; category?: string }>({});
  const [touched, setTouched] = useState<{ title?: boolean; category?: boolean }>({});

  // Reset errors when modal opens/closes or event changes
  useEffect(() => {
    setErrors({});
    setTouched({});
  }, [show, selectedEvent]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { title?: string; category?: string } = {};

    if (!newEvent.title?.trim()) {
      newErrors.title = 'El título es requerido';
    }

    if (!newEvent.categoryId) {
      newErrors.category = 'Selecciona una categoría';
    }

    setErrors(newErrors);
    setTouched({ title: true, category: true });
    return Object.keys(newErrors).length === 0;
  };

  // Handle save with validation
  const handleSave = () => {
    if (validateForm()) {
      onSave();
    }
  };

  // Determine context for display
  const hasContext = !selectedEvent && (leadContext || clientContext);
  const contextName = leadContext?.name || clientContext?.name;
  const contextType = leadContext ? 'Lead' : clientContext ? 'Cliente' : null;
  const contextEmail = leadContext?.email || clientContext?.email;
  const contextPhone = leadContext?.phone || clientContext?.phone;

  // Helper to check if a date is today
  const isToday = (date: Date | undefined) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Get minimum time for TimePicker (current time if it's today)
  const getMinTime = (date: Date | undefined) => {
    if (!date || selectedEvent) return undefined; // No restriction when editing
    if (!isToday(date)) return undefined; // No restriction for future dates
    return new Date(); // Current time for today
  };

  // Handle start time change with auto-sync to end time
  const handleStartTimeChange = (newStartTime: Date) => {
    const newEndTime = new Date(newStartTime);
    newEndTime.setMinutes(newStartTime.getMinutes() + 30);
    onNewEventChange({
      ...newEvent,
      startTime: newStartTime,
      endTime: newEndTime,
    });
  };

  // Handle start date change (keep time, update date, sync end date)
  const handleStartDateChange = (newDate: Date) => {
    const startTime = newEvent.startTime || new Date();
    const newStartTime = new Date(newDate);
    newStartTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

    const newEndTime = new Date(newStartTime);
    newEndTime.setMinutes(newStartTime.getMinutes() + 30);

    onNewEventChange({
      ...newEvent,
      startTime: newStartTime,
      endTime: newEndTime,
    });
  };

  if (!show) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 2147483647 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        style={{ zIndex: 2147483647 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedEvent ? 'Editar Evento' : 'Nuevo Evento'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {selectedEvent
              ? 'Revisa y edita la información de este evento.'
              : 'Programa una reunión, llamada, visita o cualquier actividad.'
            }
          </p>

          {/* Context Card - Lead/Client Info (only when creating new event) */}
          {hasContext && (
            <div className="mb-6 p-4 bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-sky-100 dark:bg-sky-900/50 rounded-full flex items-center justify-center">
                  <UserIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {contextName}
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 rounded-full">
                      {contextType}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {contextEmail && (
                      <div className="flex items-center gap-1.5">
                        <EnvelopeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{contextEmail}</span>
                      </div>
                    )}
                    {contextPhone && (
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{contextPhone}</span>
                      </div>
                    )}
                  </div>
                </div>
                {quoteContext && (
                  <div className="flex-shrink-0 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-1.5 text-xs">
                      <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          Cot. {quoteContext.number}
                        </div>
                        {quoteContext.unitName && (
                          <div className="text-gray-500 dark:text-gray-400">
                            {quoteContext.unitName}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newEvent.title || ''}
                onChange={(e) => {
                  onNewEventChange({ ...newEvent, title: e.target.value });
                  if (touched.title && e.target.value.trim()) {
                    setErrors(prev => ({ ...prev, title: undefined }));
                  }
                }}
                onBlur={() => setTouched(prev => ({ ...prev, title: true }))}
                placeholder="Ej: Reunión con María González"
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent ${
                  errors.title && touched.title
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-700'
                }`}
              />
              {errors.title && touched.title && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <ExclamationCircleIcon className="h-4 w-4" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* Lead Information (only shown when editing) */}
            {selectedEvent && selectedEvent.leadId && (
              <div className="bg-gradient-to-r from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  Información del Lead
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-600 dark:text-blue-300 font-medium">Cliente:</span>
                    <span className="text-blue-800 dark:text-blue-100">{selectedEvent.leadName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-600 dark:text-blue-300 font-medium">Tipo:</span>
                    <span className="text-blue-800 dark:text-blue-100 capitalize">{selectedEvent.type}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Categoría del Evento <span className="text-red-500">*</span>
              </label>
              <div className={`grid grid-cols-3 gap-2 mt-2 ${errors.category && touched.category ? 'ring-2 ring-red-500 rounded-lg p-1' : ''}`}>
                {eventCategories.map((category) => {
                  const Icon = category.icon || UserIcon;
                  const isSelected = newEvent.categoryId === category.id;

                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => {
                        onNewEventChange({
                          ...newEvent,
                          categoryId: category.id,
                          type: category.id.includes('meeting') ? 'meeting' :
                                category.id === 'call' ? 'call' :
                                category.id.includes('visit') ? 'visit' :
                                category.id === 'follow-up' ? 'follow-up' :
                                category.id === 'launch-event' ? 'launch' :
                                category.id === 'presentation' ? 'presentation' :
                                category.id === 'training' ? 'training' : 'other'
                        });
                        setErrors(prev => ({ ...prev, category: undefined }));
                      }}
                      className={`p-3 rounded-lg border-2 transition-all hover:scale-105
                        ${category.bgColor} ${category.color} ${category.darkBgColor} ${category.darkColor}
                        ${isSelected
                          ? `${category.borderColor} ${category.darkBorderColor} ring-2 ring-offset-1 ring-current shadow-md`
                          : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Icon className="w-5 h-5" />
                        <span className="text-xs font-medium text-center">{category.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {errors.category && touched.category && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <ExclamationCircleIcon className="h-4 w-4" />
                  {errors.category}
                </p>
              )}
            </div>

            {/* Date and Time - simplified version */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Fecha y Hora</h4>

              {/* Start Date/Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Inicio</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={newEvent.startTime?.toLocaleDateString('es-ES')}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer"
                      onClick={() => setShowStartDateCalendar(!showStartDateCalendar)}
                    />
                    {showStartDateCalendar && (
                      <MiniCalendar
                        selectedDate={newEvent.startTime || new Date()}
                        onDateSelect={handleStartDateChange}
                        onClose={() => setShowStartDateCalendar(false)}
                        disablePast={!selectedEvent}
                      />
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={newEvent.startTime?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer"
                      onClick={() => setShowStartTimePicker(!showStartTimePicker)}
                    />
                    {showStartTimePicker && (
                      <TimePicker
                        selectedTime={newEvent.startTime || new Date()}
                        onTimeSelect={handleStartTimeChange}
                        onClose={() => setShowStartTimePicker(false)}
                        interval={30}
                        minTime={getMinTime(newEvent.startTime)}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* End Date/Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fin</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={newEvent.endTime?.toLocaleDateString('es-ES')}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer"
                      onClick={() => setShowEndDateCalendar(!showEndDateCalendar)}
                    />
                    {showEndDateCalendar && (
                      <MiniCalendar
                        selectedDate={newEvent.endTime || new Date()}
                        onDateSelect={(date) => {
                          const endTime = newEvent.endTime || new Date();
                          const newEndTime = new Date(date);
                          newEndTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
                          onNewEventChange({ ...newEvent, endTime: newEndTime });
                        }}
                        onClose={() => setShowEndDateCalendar(false)}
                        disablePast={!selectedEvent}
                      />
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={newEvent.endTime?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer"
                      onClick={() => setShowEndTimePicker(!showEndTimePicker)}
                    />
                    {showEndTimePicker && (
                      <TimePicker
                        selectedTime={newEvent.endTime || new Date()}
                        onTimeSelect={(time) => onNewEventChange({ ...newEvent, endTime: time })}
                        onClose={() => setShowEndTimePicker(false)}
                        interval={30}
                        minTime={newEvent.startTime ? new Date(newEvent.startTime.getTime() + 30 * 60000) : undefined}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ubicación</label>
              <input
                type="text"
                value={newEvent.location || ''}
                onChange={(e) => onNewEventChange({ ...newEvent, location: e.target.value })}
                placeholder="Oficina, dirección o lugar de encuentro"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descripción</label>
              <textarea
                value={newEvent.description || ''}
                onChange={(e) => onNewEventChange({ ...newEvent, description: e.target.value })}
                placeholder="Detalles adicionales de la cita..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            {selectedEvent && (
              <button
                onClick={() => {
                  if (confirm('¿Estás seguro de que deseas eliminar este evento?')) {
                    onDelete();
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg focus:ring-2 focus:ring-red-500 transition-colors flex items-center gap-2"
              >
                <TrashIcon className="h-4 w-4" />
                Eliminar
              </button>
            )}

            <div className="flex gap-3 ml-auto">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                {selectedEvent ? 'Guardar cambios' : 'Crear Evento'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
